import React from 'react';
import cn from 'classnames';
import { Field } from 'react-final-form';

import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IRequirementNpa } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementNpa';
import { IRequirementStructuralUnit } from 'APIPackage/RequirementsNpaModule/interfaces/IRequirementStructuralUnit';
import {
  getFieldArrayErrorMessage,
  hasFieldArrayItemErrors,
} from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';

import { IKnmPmFormRequirementsNpa } from '../../MainModule/interfaces/IKnmPmFormRequirementsNpa';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import styles from './PmRequirements.module.scss';
import collapseStyles from '../../../KnmPackage/MainModule/components/KnmCollapse.module.scss';

interface IPmRequirementsItemProps {
  fieldValue: IKnmPmFormRequirementsNpa;
  index: number;
  isFieldTouched?: boolean;
  isLaterThanNpaValidationSetting: boolean;
  name: string;
  npaErrorField?: IRequirementsNpaFieldError;
  onAddMandatory: (npa: IRequirementNpa, su: IRequirementStructuralUnit, fieldName: string) => void;
  onRemoveMandatory: (parentName: string, index: number) => void;
  onRemoveRequirement: (index: number) => void;
  watchOnly: boolean;
  isImpossibilityOfPrevious?: boolean;
}

interface IRequirementsNpaFieldError {
  npa?: string;
  mandatory?: string | Array<{ id?: string }>;
}

export const PmRequirementsItem: React.FC<IPmRequirementsItemProps> = React.memo(
  ({
    fieldValue,
    index,
    isFieldTouched,
    isLaterThanNpaValidationSetting,
    name,
    npaErrorField,
    onAddMandatory,
    onRemoveMandatory,
    onRemoveRequirement,
    watchOnly,
    isImpossibilityOfPrevious,
  }) => {
    const { npa, structuralUnit } = fieldValue;

    if (!npa || !structuralUnit) {
      return null;
    }

    const npaError = npaErrorField?.npa;
    const mandatoryError = getFieldArrayErrorMessage(npaErrorField?.mandatory);
    const isMandatoryItemError = hasFieldArrayItemErrors(npaErrorField?.mandatory);

    const hasInvalidRiscIndicatorsFilter = !!fieldValue.mandatory?.find(m => m.hasInvalidRiscIndicatorsFilter);
    const hasInvalidControlObjectFilter = !!fieldValue.mandatory?.find(m => m.hasInvalidControlObjectFilter);
    const hasInvalidKnoSupervisionFilter = !!fieldValue.mandatory?.find(
      m => m.hasInvalidKno || m.hasInvalidSupervision,
    );

    return (
      <div>
        <div
          className={cn(styles.RequirementsNpaItem, {
            [styles.RequirementsNpaItemError]:
              (isFieldTouched && (npaError || mandatoryError || isMandatoryItemError)) ||
              (isLaterThanNpaValidationSetting &&
                (hasInvalidRiscIndicatorsFilter || hasInvalidControlObjectFilter || hasInvalidKnoSupervisionFilter)),
          })}
        >
          <KnmCollapse
            id={`requirementsNpa[${index}]`}
            theme="fields"
            title={
              <div className={styles.CollapseTitle}>
                <span className={styles.CollapseTitleGrey}>НПА №</span>
                {npa.number}
                <span className={styles.CollapseTitleGrey}>от</span>
                {npa.acceptanceDate}
              </div>
            }
            onClose={watchOnly ? undefined : () => onRemoveRequirement(index)}
          >
            <>
              <Field name={`${name}.npa`}>{() => null}</Field>
              <Field name={`${name}.mandatory`} subscription={{}}>
                {() => null}
              </Field>
              <div className={styles.NpaName}>{npa.title}</div>
              <div className={styles.StructureUnitTitle}>
                <span>Структурная единица</span>
                <span>{structuralUnit.title}</span>
              </div>

              <div className={styles.BeforeMandatoryBlock}>
                Содержания требований
                {!watchOnly && !isImpossibilityOfPrevious && (
                  <span className="shared-no-wrap-text">
                    <Button
                      className="shared-btn-add"
                      text="Добавить"
                      type="button"
                      variant="link"
                      color="primary"
                      id="pmRequirementsAddButton"
                      onClick={() => onAddMandatory(npa, structuralUnit, name)}
                    />
                  </span>
                )}
              </div>

              {isFieldTouched && mandatoryError && (
                <p className="shared-input-error shared-row__small-margin">{mandatoryError}</p>
              )}

              {fieldValue.mandatory?.map((mandatory, mIndex) => {
                const mandatoryItemError =
                  Array.isArray(npaErrorField?.mandatory) && !getFieldArrayErrorMessage(npaErrorField?.mandatory)
                    ? npaErrorField?.mandatory[mIndex]?.id
                    : undefined;

                return (
                  <div key={mandatory.id || mIndex}>
                    <div
                      className={cn(styles.MandatoryBlock, {
                        [styles.MandatoryBlockError]:
                          (mandatoryItemError && isFieldTouched) ||
                          (isLaterThanNpaValidationSetting &&
                            (mandatory?.hasInvalidControlObjectFilter ||
                              mandatory?.hasInvalidRiscIndicatorsFilter ||
                              mandatory.hasInvalidKno ||
                              mandatory.hasInvalidSupervision)),
                      })}
                    >
                      <div className={styles.MandatoryBlockHeader}>
                        Содержание требования
                        {!watchOnly && (
                          <button
                            type="button"
                            className={collapseStyles.DeleteButton}
                            onClick={() => onRemoveMandatory(name, mIndex)}
                          >
                            <span className={collapseStyles.DeleteButtonIcon} />
                          </button>
                        )}
                      </div>
                      <div className={styles.MandatoryBlockBody}>{mandatory.title}</div>
                    </div>
                    {isFieldTouched && mandatoryItemError && (
                      <p className="shared-input-error shared-row__small-margin">{mandatoryItemError}</p>
                    )}
                    {isLaterThanNpaValidationSetting &&
                      (mandatory.hasInvalidKno || mandatory.hasInvalidSupervision) && (
                        <p className="shared-input-error shared-row__small-margin">
                          Требование не соответствует выбранному ВК и КНО
                        </p>
                      )}
                    {isLaterThanNpaValidationSetting && mandatory.hasInvalidControlObjectFilter && (
                      <p className="shared-input-error shared-row__small-margin">
                        Требование не соответствует выбранным типу, виду и подвиду объекта контроля
                      </p>
                    )}
                    {isLaterThanNpaValidationSetting && mandatory.hasInvalidRiscIndicatorsFilter && (
                      <p className="shared-input-error shared-row__small-margin">
                        Требование не соответствует выбранному индикатору риска
                      </p>
                    )}
                  </div>
                );
              })}
            </>
          </KnmCollapse>
        </div>

        {isFieldTouched && npaError && <p className="shared-input-error shared-row__small-margin">{npaError}</p>}
      </div>
    );
  },
);
